import { IProduct } from './../interfaces/IProduct';
import { RootState } from './../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  IProductCategory,
  IProductResponse,
  IProductFilter,
  IProductWriteRequest,
} from '../interfaces/IProduct';
import { ClientCreateThirdStep } from '../models/IClient/IClient';
import { API_BASE_URL } from '../helpers/common';

export interface IInvalidAttributeProducts {
  count: number;
  list: Array<{
    id: number;
    sku: string | null;
    stockOwnerId: number | null;
  }>;
}

export const productsApi = createApi({
  reducerPath: 'productsApi',
  tagTypes: ['Product'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    /*
    * @deprecated use getProductsList instead or categoriesApi
     */
    getProductsCategories: build.query<IProductCategory[], string>({
      transformResponse: (response: { data: IProductCategory[] }) =>
        response.data,
      query: () => ({
        url: '/products/list',
        method: 'GET',
      }),
    }),
    getProductsList: build.query<
      IProductResponse,
      IProductFilter
    >({
      transformResponse: (response: { data: IProductResponse }) =>
        response.data,
      query: (params) => ({
        url: '/products/list',
        method: 'GET',
        params: {
          ...params,
          category_id: params.category        
        },
      }),
      providesTags: () => ['Product'],
    }),
    getInvalidAttributeProducts: build.query<IInvalidAttributeProducts, void>({
      transformResponse: (response: { data: IInvalidAttributeProducts }) =>
        response.data,
      query: () => ({
        url: '/products/invalid-attributes',
        method: 'GET',
      }),
      providesTags: () => ['Product'],
    }),
    getProductById: build.query<IProduct, { id: number }>({
      transformResponse: (response: { data: IProduct }) => response.data,
      query: ({ id }) => ({
        url: `/products/${id}`,
        method: 'GET',
      }),
      providesTags: () => ['Product'],
    }),
    getProductsShortList: build.query<IProduct[], string | void>({
      transformResponse: (response: { data: IProduct[] }) => response.data,
      query: () => ({
        url: '/products/short-list',
        method: 'GET',
      }),
      providesTags: () => ['Product'],
    }),
    createProduct: build.mutation<IProduct, IProductWriteRequest>({
      transformResponse: (response: { data: IProduct }) => response.data,
      query: (newProduct) => ({
        url: '/products/create',
        method: 'POST',
        body: newProduct,
      }),
      invalidatesTags: ['Product'],
    }),
    updateProduct: build.mutation<
      IProduct,
      { product: IProductWriteRequest; id: number }
    >({
      transformResponse: (response: { data: IProduct }) => response.data,
      query: ({ product, id }) => ({
        url: `/products/${id}`,
        method: 'PATCH',
        body: product,
      }),
      invalidatesTags: ['Product'],
    }),  
    updateProductsPricing: build.mutation<
      any,
      { clientId: string; data: ClientCreateThirdStep }
    >({
      transformResponse: (response: { data: IProduct }) => response.data,
      query: ({ clientId, data }) => {
        return {
          url: `clients/${clientId}/pricing`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: ['Product'],
    }),
    deleteProduct: build.mutation<IProduct, number>({
      query: (productId) => {
        return {
          url: `/products/${productId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Product'],
    }),
    // exportProduсts: build.query<
    //   any,
    //   { search: string; ids: number[]; category_id: string }
    // >({
    //   query: ({ search, ids, category_id }) => {
    //     return {
    //       url: `/api/products/export`,
    //       method: "GET",
    //       params: {
    //         search,
    //         ids,
    //         category_id,
    //       },
    //     };
    //   },
    // }),
  }),
});
